/**
 * StickyMobileClaimCTA — Iter V2 Certificate Claim.
 *
 * Appears on mobile only, once the primary claim card has scrolled off
 * screen, and hides again when the final claim card enters view or the
 * certificate has already been claimed.
 *
 * Uses IntersectionObserver so the visibility state is derived from the
 * DOM, not fragile scroll math.
 *
 * Respects the iOS safe-area inset via env(safe-area-inset-bottom).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCollectorAuth } from "@/context/CollectorAuthContext";

export default function StickyMobileClaimCTA({ certNumber, primaryRef, finalRef, hidden }) {
  const { collector } = useCollectorAuth();
  const [primaryVisible, setPrimaryVisible] = useState(true);
  const [finalVisible, setFinalVisible] = useState(false);

  useEffect(() => {
    const targets = [];
    if (primaryRef?.current) {
      const io = new IntersectionObserver(
        ([e]) => setPrimaryVisible(e.isIntersecting),
        { threshold: 0.05 },
      );
      io.observe(primaryRef.current);
      targets.push(io);
    }
    if (finalRef?.current) {
      const io = new IntersectionObserver(
        ([e]) => setFinalVisible(e.isIntersecting),
        { threshold: 0.05 },
      );
      io.observe(finalRef.current);
      targets.push(io);
    }
    return () => targets.forEach((io) => io.disconnect());
  }, [primaryRef, finalRef]);

  if (hidden) return null;
  const show = !primaryVisible && !finalVisible;
  if (!show) return null;

  const href = collector
    ? `/dashboard/claim?cert=${encodeURIComponent(certNumber)}`
    : `/register?claim=${encodeURIComponent(certNumber)}`;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 md:hidden pointer-events-none"
      data-testid="sticky-claim-cta"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="pointer-events-auto mx-3 mb-3 rounded-2xl border-2 border-[var(--ink)] bg-[var(--ink)] shadow-[0_10px_30px_rgba(0,0,0,0.35)] p-2">
        <Link
          to={href}
          data-testid="sticky-claim-cta-btn"
          className="flex w-full items-center justify-center rounded-xl bg-[var(--panini-red)] hover:bg-[var(--panini-red-deep)] active:translate-y-[1px] text-white font-display uppercase tracking-wide text-base px-4 py-3.5"
        >
          Claim My Profile
        </Link>
      </div>
    </div>
  );
}
